import React, { useState } from 'react';

interface AddDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (departmentData: any) => void;
  onAddChildCompany?: (parentCompany: any) => void; // Add callback for child company creation
  companies: any[];
  loadingCompanies: boolean;
  formLoading: boolean;
  formError: string | null;
  formSuccess: string | null;
  userRole: 'SUPER_ADMIN' | 'ADMIN' | 'COMPANY_OWNER' | 'USER';
  editingMode?: boolean;
  initialData?: any;
}

const AddDepartmentModal: React.FC<AddDepartmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onAddChildCompany,
  companies,
  loadingCompanies,
  formLoading,
  formError,
  formSuccess,
  userRole,
  editingMode = false,
  initialData = null
}) => {
  const [departmentData, setDepartmentData] = useState(() => {
    let initialCompanyId = '';
    if (initialData) {
      if (initialData.companyId && typeof initialData.companyId === 'object') {
        // Handle populated company object
        initialCompanyId = initialData.companyId._id || '';
      } else {
        // Handle string ID or no ID
        initialCompanyId = initialData.companyId || initialData._id || '';
      }
    }
    return {
      name: initialData?.name || '',
      companyId: initialCompanyId
    };
  });

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDepartmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!departmentData.name.trim()) {
      // In a real implementation, you would show an error message
      alert('Department name is required');
      return;
    }

    // For SUPER_ADMIN, company selection is required
    if (userRole === 'SUPER_ADMIN' && !departmentData.companyId) {
      // In a real implementation, you would show an error message
      alert('Company selection is required');
      return;
    }

    // Prepare data for department creation
    const departmentDataToSend: any = { ...departmentData };

    // For non-SUPER_ADMIN users, if no company ID is provided, it will be set by the parent component

    onSubmit(departmentDataToSend);
  };

  return (
    <div className="modal fade show" tabIndex={-1} style={{
      display: 'block',
      backgroundColor: 'rgba(0,0,0,0.5)',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1060,
    }}>
      <div className="modal-dialog modal-dialog-centered" style={{
        maxWidth: '700px',
        margin: '1.75rem auto',
        minHeight: 'calc(100% - 3.5rem)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="modal-content border-0 shadow-lg" style={{ width: '100%' }}>
          <div className="modal-header py-3" style={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            borderRadius: '0.3rem 0.3rem 0 0'
          }}>
            <h5 className="modal-title fw-bold">
              <i className="bi bi-building me-2"></i>
              {editingMode ? 'Edit Department' : 'Create New Department'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body py-3 text-start">
              {formError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {formSuccess}
                </div>
              )}
              <div className="row">
                {/* Show company dropdown only for SUPER_ADMIN */}
                {userRole === 'SUPER_ADMIN' && (
                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-medium text-start">
                      Company<span className="text-danger ms-1">*</span>
                    </label>
                    {loadingCompanies ? (
                      <div className="form-control form-control-lg text-start">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Loading companies...
                      </div>
                    ) : (
                      <div className="d-flex gap-2 align-items-center">
                        <select
                          className="form-select form-select-lg text-start flex-grow-1"
                          name="companyId"
                          value={departmentData.companyId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select a company</option>
                          {companies.map((company: any) => (
                            <option key={company._id} value={company._id}>
                              {company.companyName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-text">
                      Select the company for this department
                    </div>
                  </div>
                )}

                <div className="col-md-12 mb-3">
                  <label className="form-label fw-medium text-start">
                    Department Name<span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-start"
                    name="name"
                    value={departmentData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter department name"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer py-3 bg-light">
              <button
                type="button"
                className="btn btn-outline-primary px-4 py-2"
                onClick={onClose}
                disabled={formLoading}
                style={{
                  color: '#1e3c72',
                  borderColor: '#1e3c72',
                }}
                onMouseEnter={(e) => {
                  if (!formLoading) {
                    e.currentTarget.style.backgroundColor = '#1e3c72';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#1e3c72';
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4 py-2"
                disabled={formLoading}
                style={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  border: 'none'
                }}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {editingMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {editingMode ? 'Update Department' : 'Create Department'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDepartmentModal;